import type { PackageTone } from '@proffer/shared'
import type { AppTone } from '../UI'

export type { PackageTone } from '@proffer/shared'

export const packageToneBadge: Record<PackageTone, string> = {
  violet:
    'bg-violet-50 text-violet-800 ring-violet-200 dark:bg-violet-950/40 dark:text-violet-200 dark:ring-violet-900/60',
  bronze:
    'bg-orange-100 text-orange-950 ring-orange-300 dark:bg-orange-950/50 dark:text-orange-100 dark:ring-orange-700/80',
  silver:
    'bg-slate-200 text-slate-800 ring-slate-300 dark:bg-slate-700/90 dark:text-slate-100 dark:ring-slate-500',
  gold:
    'bg-yellow-100 text-yellow-950 ring-yellow-400 dark:bg-yellow-950/45 dark:text-yellow-100 dark:ring-yellow-600/70',
  sky:
    'bg-sky-50 text-sky-900 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-200 dark:ring-sky-900/60',
  emerald:
    'bg-emerald-50 text-emerald-900 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-200 dark:ring-emerald-900/60',
  rose:
    'bg-rose-50 text-rose-900 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-200 dark:ring-rose-900/60',
  indigo:
    'bg-indigo-50 text-indigo-900 ring-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-200 dark:ring-indigo-900/60',
  teal:
    'bg-teal-50 text-teal-900 ring-teal-200 dark:bg-teal-950/40 dark:text-teal-200 dark:ring-teal-900/60',
  amber:
    'bg-amber-50 text-amber-950 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-100 dark:ring-amber-800/60',
  cyan:
    'bg-cyan-50 text-cyan-900 ring-cyan-200 dark:bg-cyan-950/40 dark:text-cyan-200 dark:ring-cyan-900/60',
  fuchsia:
    'bg-fuchsia-50 text-fuchsia-900 ring-fuchsia-200 dark:bg-fuchsia-950/40 dark:text-fuchsia-200 dark:ring-fuchsia-900/60',
}

export const packageToneCard: Record<PackageTone, string> = {
  violet:
    'border-violet-200/80 bg-gradient-to-br from-violet-50 via-white to-fuchsia-50/70 shadow-violet-500/10 dark:border-violet-900/50 dark:from-violet-950/35 dark:via-zinc-950/70 dark:to-fuchsia-950/20',
  bronze:
    'border-orange-300/90 bg-gradient-to-br from-orange-100 via-amber-50 to-orange-200/80 shadow-orange-500/15 dark:border-orange-700/60 dark:from-orange-950/40 dark:via-zinc-950/70 dark:to-amber-950/25',
  silver:
    'border-slate-300/90 bg-gradient-to-br from-slate-100 via-gray-50 to-slate-200/90 shadow-slate-400/15 dark:border-slate-500/70 dark:from-slate-800/55 dark:via-zinc-950/75 dark:to-slate-900/50',
  gold:
    'border-yellow-400/90 bg-gradient-to-br from-yellow-50 via-amber-50 to-yellow-200/75 shadow-yellow-500/15 dark:border-yellow-600/55 dark:from-yellow-950/35 dark:via-zinc-950/70 dark:to-amber-950/30',
  sky:
    'border-sky-200/90 bg-gradient-to-br from-sky-50 via-white to-cyan-50/70 shadow-sky-500/10 dark:border-sky-900/50 dark:from-sky-950/35 dark:via-zinc-950/70 dark:to-cyan-950/20',
  emerald:
    'border-emerald-200/90 bg-gradient-to-br from-emerald-50 via-white to-teal-50/60 shadow-emerald-500/10 dark:border-emerald-900/50 dark:from-emerald-950/35 dark:via-zinc-950/70 dark:to-teal-950/20',
  rose:
    'border-rose-200/90 bg-gradient-to-br from-rose-50 via-white to-pink-50/70 shadow-rose-500/10 dark:border-rose-900/50 dark:from-rose-950/35 dark:via-zinc-950/70 dark:to-pink-950/20',
  indigo:
    'border-indigo-200/90 bg-gradient-to-br from-indigo-50 via-white to-violet-50/60 shadow-indigo-500/10 dark:border-indigo-900/50 dark:from-indigo-950/35 dark:via-zinc-950/70 dark:to-violet-950/20',
  teal:
    'border-teal-200/90 bg-gradient-to-br from-teal-50 via-white to-emerald-50/60 shadow-teal-500/10 dark:border-teal-900/50 dark:from-teal-950/35 dark:via-zinc-950/70 dark:to-emerald-950/20',
  amber:
    'border-amber-300/90 bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-100/80 shadow-amber-500/12 dark:border-amber-800/55 dark:from-amber-950/35 dark:via-zinc-950/70 dark:to-yellow-950/25',
  cyan:
    'border-cyan-200/90 bg-gradient-to-br from-cyan-50 via-white to-sky-50/70 shadow-cyan-500/10 dark:border-cyan-900/50 dark:from-cyan-950/35 dark:via-zinc-950/70 dark:to-sky-950/20',
  fuchsia:
    'border-fuchsia-200/90 bg-gradient-to-br from-fuchsia-50 via-white to-pink-50/70 shadow-fuchsia-500/10 dark:border-fuchsia-900/50 dark:from-fuchsia-950/35 dark:via-zinc-950/70 dark:to-pink-950/20',
}

export const packageToneDot: Record<PackageTone, string> = {
  violet: 'bg-violet-500',
  bronze: 'bg-orange-600',
  silver: 'bg-slate-400',
  gold: 'bg-yellow-500',
  sky: 'bg-sky-500',
  emerald: 'bg-emerald-500',
  rose: 'bg-rose-500',
  indigo: 'bg-indigo-500',
  teal: 'bg-teal-500',
  amber: 'bg-amber-500',
  cyan: 'bg-cyan-500',
  fuchsia: 'bg-fuchsia-500',
}

export const packageToneLink: Record<PackageTone, string> = {
  violet:
    'text-violet-700 hover:text-violet-500 dark:text-violet-300 dark:hover:text-violet-200',
  bronze:
    'text-orange-800 hover:text-orange-600 dark:text-orange-300 dark:hover:text-orange-200',
  silver:
    'text-slate-700 hover:text-slate-500 dark:text-slate-300 dark:hover:text-slate-100',
  gold: 'text-yellow-800 hover:text-yellow-600 dark:text-yellow-300 dark:hover:text-yellow-100',
  sky: 'text-sky-800 hover:text-sky-600 dark:text-sky-300 dark:hover:text-sky-200',
  emerald:
    'text-emerald-800 hover:text-emerald-600 dark:text-emerald-300 dark:hover:text-emerald-200',
  rose: 'text-rose-800 hover:text-rose-600 dark:text-rose-300 dark:hover:text-rose-200',
  indigo:
    'text-indigo-800 hover:text-indigo-600 dark:text-indigo-300 dark:hover:text-indigo-200',
  teal: 'text-teal-800 hover:text-teal-600 dark:text-teal-300 dark:hover:text-teal-200',
  amber:
    'text-amber-900 hover:text-amber-700 dark:text-amber-300 dark:hover:text-amber-200',
  cyan: 'text-cyan-800 hover:text-cyan-600 dark:text-cyan-300 dark:hover:text-cyan-200',
  fuchsia:
    'text-fuchsia-800 hover:text-fuchsia-600 dark:text-fuchsia-300 dark:hover:text-fuchsia-200',
}

export const packageToneAccentText: Record<PackageTone, string> = {
  violet: 'text-violet-800 dark:text-violet-200',
  bronze: 'text-orange-900 dark:text-orange-200',
  silver: 'text-slate-800 dark:text-slate-200',
  gold: 'text-yellow-900 dark:text-yellow-200',
  sky: 'text-sky-900 dark:text-sky-200',
  emerald: 'text-emerald-900 dark:text-emerald-200',
  rose: 'text-rose-900 dark:text-rose-200',
  indigo: 'text-indigo-900 dark:text-indigo-200',
  teal: 'text-teal-900 dark:text-teal-200',
  amber: 'text-amber-950 dark:text-amber-200',
  cyan: 'text-cyan-900 dark:text-cyan-200',
  fuchsia: 'text-fuchsia-900 dark:text-fuchsia-200',
}

const packageToneSelectionRing: Record<PackageTone, string> = {
  violet: 'ring-violet-500',
  bronze: 'ring-orange-500',
  silver: 'ring-slate-400',
  gold: 'ring-yellow-500',
  sky: 'ring-sky-500',
  emerald: 'ring-emerald-500',
  rose: 'ring-rose-500',
  indigo: 'ring-indigo-500',
  teal: 'ring-teal-500',
  amber: 'ring-amber-500',
  cyan: 'ring-cyan-500',
  fuchsia: 'ring-fuchsia-500',
}

export function packageToneSelectionRingClass(tone: PackageTone): string {
  return packageToneSelectionRing[tone]
}

export const packageToneDetail: Record<
  PackageTone,
  {
    badge: string
    panel: string
    navCard: string
    activeRing: string
    dot: string
    button: string
  }
> = {
  violet: {
    badge: packageToneBadge.violet,
    panel:
      'border-violet-200 bg-violet-50/70 dark:border-violet-900/50 dark:bg-violet-950/20',
    navCard:
      'border-violet-200 bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 text-violet-950 dark:border-violet-900/50 dark:from-violet-950/40 dark:via-zinc-950/80 dark:to-fuchsia-950/20 dark:text-violet-100',
    activeRing: 'ring-2 ring-violet-500/45 shadow-violet-500/20',
    dot: packageToneDot.violet,
    button: 'bg-violet-600 text-white hover:bg-violet-500 shadow-violet-600/20',
  },
  bronze: {
    badge: packageToneBadge.bronze,
    panel:
      'border-orange-300 bg-orange-50/80 dark:border-orange-800/60 dark:bg-orange-950/25',
    navCard:
      'border-orange-300 bg-gradient-to-br from-orange-100 via-amber-50 to-orange-200/70 text-orange-950 dark:border-orange-700/60 dark:from-orange-950/40 dark:via-zinc-950/80 dark:to-amber-950/25 dark:text-orange-100',
    activeRing: 'ring-2 ring-orange-500/45 shadow-orange-500/20',
    dot: packageToneDot.bronze,
    button: 'bg-orange-600 text-white hover:bg-orange-500 shadow-orange-600/20',
  },
  silver: {
    badge: packageToneBadge.silver,
    panel:
      'border-slate-300 bg-slate-100/80 dark:border-slate-600 dark:bg-slate-900/50',
    navCard:
      'border-slate-300 bg-gradient-to-br from-slate-100 via-gray-50 to-slate-200 text-slate-900 dark:border-slate-500 dark:from-slate-800/70 dark:via-zinc-950/80 dark:to-slate-900 dark:text-slate-100',
    activeRing: 'ring-2 ring-slate-400/50 shadow-slate-400/20',
    dot: packageToneDot.silver,
    button:
      'bg-slate-600 text-white hover:bg-slate-500 shadow-slate-600/20 dark:bg-slate-300 dark:text-slate-900',
  },
  gold: {
    badge: packageToneBadge.gold,
    panel:
      'border-yellow-400 bg-yellow-50/80 dark:border-yellow-700/50 dark:bg-yellow-950/25',
    navCard:
      'border-yellow-400 bg-gradient-to-br from-yellow-50 via-amber-50 to-yellow-200/80 text-yellow-950 dark:border-yellow-600/55 dark:from-yellow-950/35 dark:via-zinc-950/80 dark:to-amber-950/30 dark:text-yellow-100',
    activeRing: 'ring-2 ring-yellow-500/45 shadow-yellow-500/20',
    dot: packageToneDot.gold,
    button: 'bg-yellow-600 text-white hover:bg-yellow-500 shadow-yellow-600/20',
  },
  sky: {
    badge: packageToneBadge.sky,
    panel: 'border-sky-200 bg-sky-50/70 dark:border-sky-900/50 dark:bg-sky-950/20',
    navCard:
      'border-sky-200 bg-gradient-to-br from-sky-50 via-white to-cyan-50 text-sky-950 dark:border-sky-900/50 dark:from-sky-950/40 dark:via-zinc-950/80 dark:to-cyan-950/20 dark:text-sky-100',
    activeRing: 'ring-2 ring-sky-500/45 shadow-sky-500/20',
    dot: packageToneDot.sky,
    button: 'bg-sky-600 text-white hover:bg-sky-500 shadow-sky-600/20',
  },
  emerald: {
    badge: packageToneBadge.emerald,
    panel:
      'border-emerald-200 bg-emerald-50/70 dark:border-emerald-900/50 dark:bg-emerald-950/20',
    navCard:
      'border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-teal-50 text-emerald-950 dark:border-emerald-900/50 dark:from-emerald-950/40 dark:via-zinc-950/80 dark:to-teal-950/20 dark:text-emerald-100',
    activeRing: 'ring-2 ring-emerald-500/45 shadow-emerald-500/20',
    dot: packageToneDot.emerald,
    button: 'bg-emerald-600 text-white hover:bg-emerald-500 shadow-emerald-600/20',
  },
  rose: {
    badge: packageToneBadge.rose,
    panel: 'border-rose-200 bg-rose-50/70 dark:border-rose-900/50 dark:bg-rose-950/20',
    navCard:
      'border-rose-200 bg-gradient-to-br from-rose-50 via-white to-pink-50 text-rose-950 dark:border-rose-900/50 dark:from-rose-950/40 dark:via-zinc-950/80 dark:to-pink-950/20 dark:text-rose-100',
    activeRing: 'ring-2 ring-rose-500/45 shadow-rose-500/20',
    dot: packageToneDot.rose,
    button: 'bg-rose-600 text-white hover:bg-rose-500 shadow-rose-600/20',
  },
  indigo: {
    badge: packageToneBadge.indigo,
    panel:
      'border-indigo-200 bg-indigo-50/70 dark:border-indigo-900/50 dark:bg-indigo-950/20',
    navCard:
      'border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-violet-50 text-indigo-950 dark:border-indigo-900/50 dark:from-indigo-950/40 dark:via-zinc-950/80 dark:to-violet-950/20 dark:text-indigo-100',
    activeRing: 'ring-2 ring-indigo-500/45 shadow-indigo-500/20',
    dot: packageToneDot.indigo,
    button: 'bg-indigo-600 text-white hover:bg-indigo-500 shadow-indigo-600/20',
  },
  teal: {
    badge: packageToneBadge.teal,
    panel: 'border-teal-200 bg-teal-50/70 dark:border-teal-900/50 dark:bg-teal-950/20',
    navCard:
      'border-teal-200 bg-gradient-to-br from-teal-50 via-white to-emerald-50 text-teal-950 dark:border-teal-900/50 dark:from-teal-950/40 dark:via-zinc-950/80 dark:to-emerald-950/20 dark:text-teal-100',
    activeRing: 'ring-2 ring-teal-500/45 shadow-teal-500/20',
    dot: packageToneDot.teal,
    button: 'bg-teal-600 text-white hover:bg-teal-500 shadow-teal-600/20',
  },
  amber: {
    badge: packageToneBadge.amber,
    panel:
      'border-amber-300 bg-amber-50/80 dark:border-amber-800/55 dark:bg-amber-950/25',
    navCard:
      'border-amber-300 bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-100 text-amber-950 dark:border-amber-800/55 dark:from-amber-950/35 dark:via-zinc-950/80 dark:to-yellow-950/25 dark:text-amber-100',
    activeRing: 'ring-2 ring-amber-500/45 shadow-amber-500/20',
    dot: packageToneDot.amber,
    button: 'bg-amber-600 text-white hover:bg-amber-500 shadow-amber-600/20',
  },
  cyan: {
    badge: packageToneBadge.cyan,
    panel: 'border-cyan-200 bg-cyan-50/70 dark:border-cyan-900/50 dark:bg-cyan-950/20',
    navCard:
      'border-cyan-200 bg-gradient-to-br from-cyan-50 via-white to-sky-50 text-cyan-950 dark:border-cyan-900/50 dark:from-cyan-950/40 dark:via-zinc-950/80 dark:to-sky-950/20 dark:text-cyan-100',
    activeRing: 'ring-2 ring-cyan-500/45 shadow-cyan-500/20',
    dot: packageToneDot.cyan,
    button: 'bg-cyan-600 text-white hover:bg-cyan-500 shadow-cyan-600/20',
  },
  fuchsia: {
    badge: packageToneBadge.fuchsia,
    panel:
      'border-fuchsia-200 bg-fuchsia-50/70 dark:border-fuchsia-900/50 dark:bg-fuchsia-950/20',
    navCard:
      'border-fuchsia-200 bg-gradient-to-br from-fuchsia-50 via-white to-pink-50 text-fuchsia-950 dark:border-fuchsia-900/50 dark:from-fuchsia-950/40 dark:via-zinc-950/80 dark:to-pink-950/20 dark:text-fuchsia-100',
    activeRing: 'ring-2 ring-fuchsia-500/45 shadow-fuchsia-500/20',
    dot: packageToneDot.fuchsia,
    button: 'bg-fuchsia-600 text-white hover:bg-fuchsia-500 shadow-fuchsia-600/20',
  },
}

/** Για AdminBadge — όσες παλέτες υπάρχουν και στο AppTone. */
export function packageToneToAppTone(tone: PackageTone): AppTone {
  if (tone === 'rose') return 'red'
  if (tone === 'indigo') return 'violet'
  if (tone === 'teal') return 'emerald'
  if (tone === 'cyan') return 'sky'
  if (tone === 'fuchsia') return 'violet'
  return tone as AppTone
}

/** AppBadge / AppTone — μεταλλική παλέτα (παλιά ονομασία) */
export type MetallicAppTone = 'bronze' | 'silver' | 'gold'

export const metallicAppToneBadge: Record<MetallicAppTone, string> = {
  bronze: packageToneBadge.bronze,
  silver: packageToneBadge.silver,
  gold: packageToneBadge.gold,
}
